import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  Building2,
  IdCard,
  Loader2,
  MapPin,
  Search,
  Truck,
  Users,
  type LucideIcon,
} from 'lucide-react';
// Deep imports, not the package barrels — this component is mounted by AppShell,
// so it is on every page's boot path. `@attendance/ui`'s barrel drags Dialog's
// CJS focus-trap, and `@attendance/shared`'s `export *`s all the zod schemas.
// `SearchEntityType` is type-only, so it is erased and costs nothing.
import { cn } from '@attendance/ui/utils';
import { insetFocusRing } from '@attendance/ui/focus';
import { SEARCH_MIN_QUERY_LENGTH } from '@attendance/shared/searchLimits';
import type { SearchEntityType } from '@attendance/shared';
import { useAuthStore } from '../../stores/authStore.js';
import { useFeatures } from '../../stores/brandingStore.js';
import { searchApi } from '../../api/search.api.js';
import { allDestinations, navLabelOf } from './navItems.js';

/** Long enough that a typed name is one request, short enough to feel instant. */
const DEBOUNCE_MS = 250;

/** Icon + heading per entity, matching the nav row each record's page sits on. */
const ENTITY_META: Record<
  SearchEntityType,
  { icon: LucideIcon; labelKey: string; label: string }
> = {
  employee: { icon: Users, labelKey: 'command.groupEmployees', label: 'Employees' },
  worksite: { icon: MapPin, labelKey: 'command.groupWorksites', label: 'Worksites' },
  department: { icon: Building2, labelKey: 'command.groupDepartments', label: 'Departments' },
  designation: { icon: IdCard, labelKey: 'command.groupDesignations', label: 'Designations' },
  supplier: { icon: Truck, labelKey: 'command.groupSuppliers', label: 'Labour suppliers' },
};

/** One selectable line. Pages and data hits are the same shape by design, so
 *  keyboard navigation crosses the boundary without knowing it is there. */
interface PaletteRow {
  key: string;
  to: string;
  label: string;
  sublabel: string | null;
  icon: LucideIcon;
}

interface PaletteSection {
  id: string;
  title: string;
  rows: PaletteRow[];
}

/**
 * Command palette — global ⌘K / Ctrl+K.
 *
 * It searches TWO things at once: the nav destinations the current role can
 * reach (as it always has), and the DATA behind them — people, worksites,
 * departments, designations, labour suppliers — via `GET /api/search`. Before
 * this, finding a record meant already knowing which screen listed it.
 *
 * Three response states are kept distinct on purpose, because this codebase has
 * repeatedly been bitten by conflating them:
 *   - LOADING — a request is in flight; the list is not yet an answer
 *   - EMPTY   — the server answered, and there is genuinely nothing
 *   - ERROR   — the search FAILED. This must never render as "no results";
 *               a failed request that looks like an empty one tells the reader
 *               the record does not exist when it may well.
 * The pages half keeps working in every one of them — a failed data search
 * degrades the palette to what it used to be, rather than breaking it.
 *
 * Accessible: role=dialog, scrim dismiss, focus moves to the input, the results
 * are an ARIA listbox driven by `aria-activedescendant` so ↑/↓/Enter/Esc work
 * across the combined list while focus stays in the text box.
 */
export function CommandPalette(): JSX.Element | null {
  const role = useAuthStore((s) => s.user?.role);
  const features = useFeatures();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Debounce the text that feeds the network request. The pages filter below
  // stays on the RAW query so typing never feels laggy for the half of the
  // palette that needs no server.
  useEffect(() => {
    const id = setTimeout(() => setDebounced(query.trim()), DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [query]);

  const canSearchData = open && debounced.length >= SEARCH_MIN_QUERY_LENGTH;

  const {
    data: results,
    isFetching,
    isError,
  } = useQuery({
    queryKey: ['global-search', debounced],
    queryFn: ({ signal }) => searchApi.global(debounced, signal),
    enabled: canSearchData,
    // A failed search must surface as an error promptly rather than sitting in
    // a retry loop that reads as "still loading".
    retry: false,
    staleTime: 30_000,
  });

  const pages = useMemo<PaletteRow[]>(() => {
    const list = allDestinations(role, features).map((d) => {
      const naming = navLabelOf(d, features);
      return { ...d, name: t(naming.labelKey, naming.label) };
    });
    const q = query.trim().toLowerCase();
    const matched = q ? list.filter((d) => d.name.toLowerCase().includes(q)) : list;
    return matched.map((d) => ({
      key: `page:${d.to}`,
      to: d.to,
      label: d.name,
      sublabel: null,
      icon: d.icon,
    }));
  }, [role, features, query, t]);

  const sections = useMemo<PaletteSection[]>(() => {
    const out: PaletteSection[] = [];
    if (pages.length > 0) {
      out.push({ id: 'pages', title: t('command.groupPages', 'Pages'), rows: pages });
    }
    // Only trust a response that belongs to the text currently in the box —
    // otherwise a slow reply for an earlier query paints stale hits under it.
    if (results && results.query === debounced) {
      results.groups.forEach((group) => {
        const meta = ENTITY_META[group.type];
        if (!meta) return;
        out.push({
          id: group.type,
          title: t(meta.labelKey, meta.label),
          rows: group.hits.map((hit) => ({
            key: `${hit.type}:${hit.id}`,
            to: hit.to,
            label: hit.label,
            sublabel: hit.sublabel,
            icon: meta.icon,
          })),
        });
      });
    }
    return out;
  }, [pages, results, debounced, t]);

  // One flat list so ↑/↓ crosses group boundaries without special cases.
  const flat = useMemo<PaletteRow[]>(() => sections.flatMap((s) => s.rows), [sections]);
  const activeIndex = flat.length === 0 ? -1 : Math.min(active, flat.length - 1);
  const optionId = (index: number): string => `command-option-${index}`;

  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (open) {
      setQuery('');
      setDebounced('');
      setActive(0);
      const id = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [open]);

  useEffect(() => setActive(0), [query]);

  // Keep the highlighted row visible as ↑/↓ walks past the fold.
  useEffect(() => {
    if (activeIndex < 0) return;
    const el = listRef.current?.querySelector(`#${CSS.escape(optionId(activeIndex))}`);
    // Feature-detected: jsdom has no scrollIntoView, and scrolling is a
    // nicety — it must never be able to take the palette down with it.
    if (el instanceof HTMLElement && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  if (!open) return null;

  function go(to: string): void {
    setOpen(false);
    navigate(to);
  }

  function onInputKey(e: ReactKeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, flat.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const row = flat[activeIndex];
      if (row) go(row.to);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  }

  const typedTooShort = query.trim().length > 0 && query.trim().length < SEARCH_MIN_QUERY_LENGTH;
  const searching = canSearchData && isFetching;
  const dataFailed = canSearchData && isError;

  // "Nothing matched" is only sayable when nothing is pending and nothing broke.
  const showEmpty = flat.length === 0 && !searching && !dataFailed && !typedTooShort;

  let rowIndex = -1;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center p-4 pt-[10vh]"
      role="dialog"
      aria-modal="true"
      aria-label={t('command.searchTitle', 'Search')}
    >
      <button
        type="button"
        className="absolute inset-0 bg-scrim/50"
        aria-hidden
        onClick={() => setOpen(false)}
        tabIndex={-1}
      />
      {/* `shadow-popover`, not the deprecated `shadow-elev-2xl` alias it used to
          take. The palette is the popover ROLE by definition — it floats over
          the page with nothing behind it but a scrim — and the popover shadow is
          the system's only two-layer one for exactly that reason: a wide soft
          pool places it above the page and a tight contact edge keeps its own
          boundary readable against the warm paper, which a single layer cannot
          do at once. Same rendered value as the alias (`--shadow-2xl` already
          resolved to `--shadow-popover`), so this names the role rather than
          changing the look. */}
      <div className="relative w-full max-w-dialog overflow-hidden rounded-modal border border-border bg-surface shadow-popover">
        <div className="flex items-center gap-element border-b border-border px-3">
          <Search size={16} className="shrink-0 text-muted-foreground" aria-hidden />
          {/* Focus: the INSET variant of the canon, not the outset one. This
              input is `h-12` in a header row flush with the top edge of an
              `overflow-hidden` dialog, so an outline drawn 2px OUTSIDE it is
              clipped away and the field is back to having no indicator at all
              — which is what it had, a bare `focus:outline-none` with no
              replacement and no `focus-within` on the wrapper. */}
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onInputKey}
            placeholder={t('command.searchPlaceholder', 'Search pages, people and records…')}
            aria-label={t('command.searchPlaceholder', 'Search pages, people and records…')}
            role="combobox"
            aria-expanded
            aria-controls="command-listbox"
            aria-autocomplete="list"
            aria-activedescendant={activeIndex >= 0 ? optionId(activeIndex) : undefined}
            className={cn(
              'h-12 w-full bg-transparent text-sm text-foreground placeholder:text-muted-foreground',
              insetFocusRing
            )}
          />
          {searching && (
            <Loader2
              size={16}
              className="shrink-0 animate-spin text-muted-foreground"
              aria-hidden
            />
          )}
        </div>
        <ul
          ref={listRef}
          id="command-listbox"
          role="listbox"
          aria-label={t('command.searchTitle', 'Search')}
          className="max-h-80 overflow-y-auto py-1"
        >
          {sections.map((section) => (
            <li key={section.id} role="presentation">
              {/* `text-eyebrow`, not `text-xs uppercase tracking-wide`. This is
                * a group heading in a list — the eyebrow role — and the system
                * has exactly ONE uppercase step for that (§1: 12px/600/+0.3px).
                * The old spelling was off-scale (`text-xs` is a raw Tailwind
                * step, not a token) and tracked at +0.05em, which is a fourth
                * uppercase treatment nobody chose. `text-eyebrow` carries the
                * uppercase itself, so the utility does not restate it. */}
              <p className="px-4 pb-1 pt-2 text-eyebrow text-muted-foreground">
                {section.title}
              </p>
              <ul role="group" aria-label={section.title}>
                {section.rows.map((row) => {
                  rowIndex += 1;
                  const index = rowIndex;
                  const isActive = index === activeIndex;
                  return (
                    <li key={row.key} role="presentation">
                      <button
                        type="button"
                        id={optionId(index)}
                        role="option"
                        aria-selected={isActive}
                        tabIndex={-1}
                        onMouseEnter={() => setActive(index)}
                        onClick={() => go(row.to)}
                        className={cn(
                          'flex w-full items-center gap-inline px-4 py-2.5 text-start text-sm transition-colors',
                          isActive
                            ? 'bg-primary/10 text-primary'
                            : 'text-foreground hover:bg-muted'
                        )}
                      >
                        <row.icon size={16} className="shrink-0" aria-hidden />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate">{row.label}</span>
                          {row.sublabel && (
                            <span
                              className={cn(
                                'block truncate text-xs',
                                isActive ? 'text-primary/70' : 'text-muted-foreground'
                              )}
                            >
                              {row.sublabel}
                            </span>
                          )}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </li>
          ))}

          {/* ERROR — deliberately its own message, and never mistakable for
              "nothing matched". The pages above keep working. */}
          {dataFailed && (
            <li
              role="presentation"
              className="flex items-start gap-element px-cell-x py-cell-y text-sm text-danger-on-surface"
            >
              <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden />
              <span role="alert">
                {t(
                  'command.searchFailed',
                  'Search failed — records could not be loaded. This is not the same as no matches.'
                )}
              </span>
            </li>
          )}

          {searching && flat.length === 0 && (
            <li
              role="presentation"
              className="px-4 py-6 text-center text-sm text-muted-foreground"
              aria-live="polite"
            >
              {t('command.searching', 'Searching…')}
            </li>
          )}

          {typedTooShort && (
            <li role="presentation" className="px-4 py-6 text-center text-sm text-muted-foreground">
              {t('command.minLength', 'Type at least {{count}} characters to search records', {
                count: SEARCH_MIN_QUERY_LENGTH,
              })}
            </li>
          )}

          {showEmpty && (
            <li role="presentation" className="px-4 py-6 text-center text-sm text-muted-foreground">
              {t('command.noMatches', 'No matching pages or records')}
            </li>
          )}

          {results?.truncated && results.query === debounced && flat.length > 0 && (
            <li
              role="presentation"
              className="border-t border-border px-4 py-2 text-xs text-muted-foreground"
            >
              {t('command.truncated', 'Showing the top matches — refine your search for more')}
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}
